import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';

interface CharacterVariantsPopupProps {
  variants: string[];
  baseChar: string;
  buttonRect: DOMRect;
  onSelect: (char: string) => void;
  onClose: () => void;
}

export function CharacterVariantsPopup({
  variants,
  baseChar,
  buttonRect,
  onSelect,
  onClose,
}: CharacterVariantsPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Calculate position - center above the key
  const popupWidth = variants.length * 44 + 16; // 44px per variant + padding
  const left = buttonRect.left + buttonRect.width / 2 - popupWidth / 2;
  const top = buttonRect.top - 70; // Position above the key

  // Ensure popup stays within viewport
  const adjustedLeft = Math.max(8, Math.min(left, window.innerWidth - popupWidth - 8));
  const adjustedTop = Math.max(8, top);

  return (
    <AnimatePresence>
      <motion.div
        ref={popupRef}
        initial={{ opacity: 0, scale: 0.9, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 10 }}
        transition={{ type: 'spring', damping: 25, stiffness: 400 }}
        className="fixed z-[9999] rounded-xl border border-border/60 bg-[#0e1118]/98 shadow-2xl backdrop-blur-xl"
        style={{
          left: adjustedLeft,
          top: adjustedTop,
        }}
      >
        {/* Glow effect */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/40 to-transparent" />

        {/* Base character indicator */}
        <div className="border-b border-border/30 px-3 py-1.5 text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand">
            {baseChar}
          </span>
        </div>

        {/* Variants grid */}
        <div className="flex gap-1 p-2">
          {variants.map((variant, index) => (
            <motion.button
              key={`${variant}-${index}`}
              type="button"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
              onClick={() => {
                onSelect(variant);
                onClose();
              }}
              className="group relative flex h-11 w-11 items-center justify-center rounded-lg border border-border/40 bg-white/[0.02] text-lg font-medium text-foreground transition-all duration-150 hover:border-brand/60 hover:bg-brand/10 hover:text-brand active:scale-95"
            >
              {variant}

              {/* Hover glow */}
              <div className="pointer-events-none absolute inset-0 rounded-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                style={{
                  background: 'radial-gradient(circle at center, rgba(249,115,22,0.15), transparent 70%)',
                }}
              />
            </motion.button>
          ))}
        </div>

        {/* Hint text */}
        <div className="border-t border-border/20 px-3 py-1.5 text-center">
          <span className="text-[10px] text-muted-foreground/60">
            Tap to select
          </span>
        </div>

        {/* Arrow pointing to the key */}
        <div
          className="absolute -bottom-2 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-border/60 bg-[#0e1118]/98"
          style={{
            left: buttonRect.left - adjustedLeft + buttonRect.width / 2,
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}
